/**
 * Buffer polyfill for browser environment
 * This ensures Buffer is available in both Node.js and browser environments
 */

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// Browser-compatible Buffer implementation
const createBrowserBuffer = () => {
  return {
    from: (data: string | Uint8Array, encoding: 'base64' | 'utf8' | 'hex' = 'utf8') => {
      if (typeof data === 'string') {
        if (encoding === 'base64') {
          // Convert base64 string to Uint8Array
          const binaryString = atob(data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          return bytes;
        } else if (encoding === 'hex') {
          // Convert hex string to Uint8Array
          const bytes = new Uint8Array(data.length / 2);
          for (let i = 0; i < data.length; i += 2) {
            bytes[i / 2] = parseInt(data.substr(i, 2), 16);
          }
          return bytes;
        } else {
          // UTF-8 encoding
          return new TextEncoder().encode(data);
        }
      } else if (data instanceof Uint8Array) {
        return data;
      }
      throw new Error('Unsupported data type for Buffer.from');
    },
    isBuffer: (obj: any) => {
      return obj instanceof Uint8Array;
    },
    alloc: (size: number, fill?: number | string) => {
      const buffer = new Uint8Array(size);
      if (fill !== undefined) {
        if (typeof fill === 'number') {
          buffer.fill(fill);
        } else {
          const fillBytes = new TextEncoder().encode(fill);
          for (let i = 0; i < size; i++) {
            buffer[i] = fillBytes[i % fillBytes.length];
          }
        }
      }
      return buffer;
    },
    concat: (list: Uint8Array[], length?: number) => {
      if (length === undefined) {
        length = list.reduce((acc, buf) => acc + buf.length, 0);
      }
      const result = new Uint8Array(length);
      let offset = 0;
      for (const buf of list) {
        result.set(buf, offset);
        offset += buf.length;
      }
      return result;
    }
  };
};

// Create a Buffer-like object that works in both environments
export const Buffer = isBrowser ? createBrowserBuffer() : globalThis.Buffer;

// Helper function to convert Uint8Array to base64 string
export const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  if (isBrowser) {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  } else {
    return Buffer.from(bytes).toString('base64');
  }
};

// Helper function to convert base64 string to Uint8Array
export const base64ToUint8Array = (base64: string): Uint8Array => {
  if (isBrowser) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } else {
    return Buffer.from(base64, 'base64');
  }
};

export default Buffer;
